import { useState, useCallback, useEffect } from 'react';
import { servicebookingFlowStore } from '../store/ServiceBookingFlowStore';
import { patientStore } from '../store/PatientStore';
import { useUserQuery, usePatientLazyQuery } from '../graphql/types-and-hooks';
import { useBookAppointmentStore } from '../store';
import { toJS } from 'mobx'; // Import toJS from mobx
import logger from '../utils/logger';
/**
 * Custom hook to provide handlers for selections in the booking flow
 */
export const useSelectionHandlers = () => {
  const [selectedServiceType, setSelectedServiceType] = useState(null);
  // eslint-disable-next-line 
  const { data: userData, refetch: refetchUserData } = useUserQuery({ variables: {} });
  // eslint-disable-next-line 
  const [patient, setData] = useBookAppointmentStore((state) => [state.patient, state.setData]);
  
  // Store selected doctor in local state
  const [appointment, setAppointment] = useState(null);
  const [getPatient] = usePatientLazyQuery();

  // Use useEffect to set the appointment data reactively
  useEffect(() => {
    // Get the selected doctor from the store and update local state
    const selectedDoctor = servicebookingFlowStore.getSelectedDoctor();
    setAppointment(selectedDoctor);
  }, []);  // You might need to add dependencies based on when this should update

  // Handler for selecting a service type
  const handleServiceTypeSelect = useCallback((serviceType) => {
    setSelectedServiceType(serviceType);
    // Update store
    servicebookingFlowStore.handleServiceTypeSelection(serviceType);
  }, []);

  // Handler for selecting an appointment type
  const handleAppointmentTypeSelect = useCallback((appointmentType) => {
    // Update store
    servicebookingFlowStore.handleAppointmentTypeSelection(appointmentType);
  }, []);

  // Handler for selecting a family member
  const handleSelectFamilyMember = useCallback(async (familyMember) => {
    // Update store
    servicebookingFlowStore.handleFamilyMemberSelection(familyMember);

    // Make sure we have appointment data before proceeding
    if (!appointment) {
      console.error('No appointment data available');
      return;
    }

    try {
      const { data: patientData } = await getPatient({
        variables: {
          input: {
            clinic_group_crm: appointment.clinic_group_crm,
            user_fk: appointment.user_fk,
            location_id: appointment.location_id,
            dob: familyMember.dob,
            first_name: familyMember.first_name,
            last_name: familyMember.last_name,
            contact_number: familyMember.phone_number,
          },
        },
      });

      const familyMemberData = {
        type: 'familyMember',
        family_member_key: familyMember,
        first_name: familyMember.first_name,
        last_name: familyMember.last_name,
        dob: familyMember.dob,
        contact_number: familyMember.phone_number,
        is_new_pt: patientData?.patient?.is_new_pt === '0' ? '0' : '1',
        last_seen_did: patientData?.patient?.last_seen_did,
      };

      logger.info('Family member data:', familyMemberData);

      patientStore.setCurrentPatient(familyMemberData);
      
      if (servicebookingFlowStore.currentStep === 1) {
        servicebookingFlowStore.goToStep(2);
      } else if (servicebookingFlowStore.currentStep === 5) {
        servicebookingFlowStore.goToStep(6);
      }

    } catch (error) {
      logger.error('Error fetching patient data:', error);
    }
  }, [appointment, getPatient]);

  // Handler for opening the create family member modal
  const handleOpenCreateFamilyMemberModal = useCallback(() => {
    servicebookingFlowStore.setShowFamilyMemberStep(true);
  }, []);

  // Handler for selecting "Myself" option
  const selectMe = useCallback(() => {
    servicebookingFlowStore.updateAppointmentData("isForUser", true);

    try {
      if (!userData?.user) {
        return;
      }

      // Use toJS to safely access the observable outside a reactive context
      const currentPatient = toJS(patientStore.currentMainLoginPatient) || {};

      const userp = {
        type: "myself",
        first_name: userData.user.first_name,
        last_name: userData.user.last_name,
        dob: userData.user.dob,
        contact_number: userData.user.phone_number,
        is_new_pt: currentPatient.is_new_pt === '0' ? '0' : '1',
        last_seen_did: currentPatient.last_seen_did || null,
      };

      logger.info('Current Patient:', userp);

      patientStore.setCurrentPatient(userp);
      // Move to next step
      servicebookingFlowStore.nextStep();

    } catch (error) {
      console.error(`Error fetching patient data: `, error);
    }
  }, [userData]);

  return {
    selectedServiceType,
    handleServiceTypeSelect,
    handleAppointmentTypeSelect,
    handleSelectFamilyMember,
    handleOpenCreateFamilyMemberModal,
    selectMe
  };
};

export default useSelectionHandlers;